import { useDeactivatePromotedFreeTrialMutation } from '@heidi/commerce/data';
import { invalidateCurrentUserComposite } from '@heidi/workspace/data';
// eslint-disable-next-line @heidi/rules/no-unguarded-apple-billing -- useMutateCheckoutV2 already guards execution internally via isAppleCompliantBuild(); this hook renders no UI
import { useMutateCheckoutV2 } from '@/hooks/mutation/useMutateCheckoutV2';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { logger } from '@/utils/logger/logger';
import { getStripeCallbackRedirect, getStripeReturnRedirect } from '@/utils/scribeHTTP';
import {
  type Period,
  FTPConversionSource,
  FTPConversionSourceURLQueryKey,
} from '@heidi/commerce/contracts';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCallback } from 'react';

interface UseTrialManagementOptions {
  period: Period;
  afterAction: () => void;
}

export function usePromotedTrialManagement({
  period,
  afterAction,
}: UseTrialManagementOptions) {
  const { t } = useTranslations();
  const {
    mutateAsync: deactivatePromotedTrialMutation,
    isPending: isDeactivateTrialPending,
  } = useDeactivatePromotedFreeTrialMutation({
    onRefreshUser: () => {
      void invalidateCurrentUserComposite();
    },
    onDowngradedNotice: () => {
      toast({
        tone: 'default',
        title: t('sideBySide.downgradedToFreeTitle'),
        description: t('sideBySide.downgradedToFreeDescription'),
      });
    },
    onDowngradeFailedNotice: () => {
      toast({
        tone: 'destructive',
        title: t('sideBySide.failedToRevertTrialTitle'),
        description: t('sideBySide.failedToRevertTrialDescription'),
      });
    },
  });

  const { mutateAsync: createCheckoutSession, isPending: isCreatingCheckout } =
    useMutateCheckoutV2();

  const { data: billingInfo } = useQueryUserBillingInfo((data) => ({
    isInPromotedFreeTrial: data.is_promoted_free_trial,
    trialEndDate: data.trial_end_date,
  }));
  const userIdQuery = useQueryUser((data) => data.kinde_user_id);

  const handleStayPro = useCallback(async () => {
    if (!userIdQuery.data) return;
    const callbackURL = new URL(getStripeCallbackRedirect());
    callbackURL.searchParams.set(
      FTPConversionSourceURLQueryKey,
      FTPConversionSource.SideBySide
    );
    void deactivatePromotedTrialMutation().catch(logger.error);
    await createCheckoutSession({
      plan_period: period,
      tier: SubscriptionTier.PRO,
      fe_endpoint: callbackURL.toString(),
      fe_return_endpoint: getStripeReturnRedirect(),
      kindeUserId: userIdQuery.data,
    });
    afterAction();
  }, [
    userIdQuery.data,
    deactivatePromotedTrialMutation,
    createCheckoutSession,
    period,
    afterAction,
  ]);

  const handleGoFree = useCallback(async () => {
    try {
      await deactivatePromotedTrialMutation();
    } catch (error) {
      logger.error(error);
    } finally {
      afterAction();
    }
  }, [deactivatePromotedTrialMutation, afterAction]);

  return {
    billingInfo,
    handleStayPro,
    handleGoFree,
    isCreatingCheckout,
    isDeactivateTrialPending,
  };
}
